import { useEffect } from "react"
import CwDataTable from "../../components/construction/CwDataTable"
import CwPageHeader from "../../components/construction/CwPageHeader"
import { t } from "../../i18n"
import { CwFlaggedPunch, CwOfficeAlert } from "../../Interfaces"
import { alertStatusLabel, punchDistanceLabel, punchKindLabel, settingsToForm, userName } from "../../utils/punchLocations"

const alerts: CwOfficeAlert[] = [
  {
    id: 11,
    type: "location_denied",
    date: "2026-10-22",
    mode: "ADMIN",
    attemptCount: 2,
    lastAttemptAt: "2026-10-22T15:00:00.000Z",
    overrideAt: "2026-10-22T16:00:00.000Z",
    overrideNote: "Phone GPS broken",
    consumedAt: null,
    dismissedAt: null,
    employee: { id: 701, firstName: "Ada", lastName: "Lovelace", employeeNum: 42 },
    jobSite: null,
    attemptedByUser: { id: 701, firstName: "Ada", lastName: "Lovelace" },
    overrideByUser: { id: 2, firstName: "Nate", lastName: "Bunney" },
    dismissedByUser: null,
  },
]

const flagged: CwFlaggedPunch[] = [
  {
    id: 3,
    workDayId: 80,
    kind: "CLOCK_OUT",
    status: "granted",
    latitude: 37.7,
    longitude: -121.1,
    accuracyM: 20,
    capturedAt: "2026-10-22T23:00:00.000Z",
    referenceKind: "office",
    distanceMiles: 6.4,
    thresholdMiles: 0.5,
    flaggedFar: true,
    reviewedAt: null,
    reviewedByUserId: null,
    createdAt: "2026-10-22T23:00:00.000Z",
    workDay: {
      id: 80,
      date: "2026-10-22",
      mode: "ADMIN",
      employee: { id: 701, firstName: "Ada", lastName: "Lovelace", employeeNum: 42 },
      jobSite: null,
    },
    actorUser: { id: 701, firstName: "Ada", lastName: "Lovelace" },
    reviewedByUser: null,
  },
]

/**
 * Unauthenticated fixture for ConstructionWalk Time clock.
 * /preview/time-clock?product=construction
 */
export default function TimeClockPreview() {
  useEffect(() => {
    document.documentElement.classList.add("construction-walk")
  }, [])
  const settings = settingsToForm({
    companyId: 12,
    address: "1200 River Road",
    city: "Modesto",
    state: "CA",
    zipcode: "95354",
    officeLatitude: 37.639,
    officeLongitude: -120.997,
    farPunchThresholdMiles: 0.5,
    adminCustomPayrollCode: "9099",
    adminPayrollJobNumber: "100",
  })

  return (
    <div className="min-h-screen p-4 md:p-8 bg-secondary-extra-light">
      <CwPageHeader title={t("timeClock.title")} subtitle={`Valley Builders · VAL · ${t("timeClock.subtitle")}`} />

      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-1">{t("timeClock.alertsTitle")}</h2>
        <p className="text-sm text-gray-600 font-source mb-3">{t("timeClock.alertsHelper")}</p>
        <CwDataTable<CwOfficeAlert>
          columns={[
            { key: "employee", label: t("timeClock.columnEmployee"), render: (row) => `${row.employee?.firstName} ${row.employee?.lastName}` },
            { key: "date", label: t("timeClock.columnDate"), render: (row) => String(row.date).slice(0, 10) },
            { key: "mode", label: t("timeClock.columnMode"), render: (row) => t("timeClock.modeAdmin") },
            { key: "status", label: t("timeClock.columnStatus"), render: (row) => alertStatusLabel(row, () => "9:00 AM PDT") },
          ]}
          rows={alerts}
          empty={t("timeClock.alertsEmpty")}
        />
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-1">{t("timeClock.flaggedTitle")}</h2>
        <p className="text-sm text-gray-600 font-source mb-3">{t("timeClock.flaggedHelper")}</p>
        <CwDataTable<CwFlaggedPunch>
          columns={[
            { key: "employee", label: t("timeClock.columnEmployee"), render: (row) => `${row.workDay.employee?.firstName} ${row.workDay.employee?.lastName}` },
            { key: "punch", label: t("timeClock.columnPunch"), render: (row) => punchKindLabel(row.kind) },
            { key: "distance", label: t("timeClock.columnDistance"), render: (row) => punchDistanceLabel(row) },
            { key: "review", label: t("timeClock.columnReview"), render: () => t("timeClock.markReviewed") },
          ]}
          rows={flagged}
          empty={t("timeClock.flaggedEmpty")}
        />
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-1">{t("timeClock.settingsTitle")}</h2>
        <p className="text-sm text-gray-600 font-source mb-3">{t("timeClock.settingsHelper")}</p>
        <dl className="bg-white rounded-2xl shadow-lg p-5 grid gap-2 md:grid-cols-2 font-source">
          <dt>{t("timeClock.addressLabel")}</dt><dd>{settings.address}</dd>
          <dt>{t("timeClock.thresholdLabel")}</dt><dd>{settings.farPunchThresholdMiles}</dd>
          <dt>{t("timeClock.customCodeLabel")}</dt><dd>{settings.adminCustomPayrollCode}</dd>
          <dt>{t("timeClock.adminJobLabel")}</dt><dd>{settings.adminPayrollJobNumber}</dd>
        </dl>
        <p className="sr-only">{userName(alerts[0].overrideByUser)}</p>
      </section>
    </div>
  )
}
